import type { ReactNode } from "react";
import { UPGRADE_COPY, UPGRADE_TITLE, can, type Feature } from "@foreman/protocol";
import type { Nav, View } from "../nav";
import { signInAvailable } from "../account";
import { BackButton, useConnection } from "../ui";
import { GitDemo } from "../guest-demo";

/** May this computer's current plan use `feature`? Unknown (an older computer that hasn't said) counts as yes: the computer is the one that enforces. */
export function useCan(computerId: string, feature: Feature): boolean {
  const { snap } = useConnection(computerId);
  const plan = snap.account?.plan;
  return plan === undefined ? true : can({ plan }, feature);
}

/** A soft light-yellow card: this needs an account. Same look for any gated feature, now or later. */
export function UpgradeNotice({ nav, then, feature, text }: { nav: Nav; then: View; feature?: Feature; text?: string }) {
  const body = text ?? (feature ? UPGRADE_COPY[feature] : "Sign in to unlock unlimited sessions, history, Git controls and agent approvals.");
  const go = () => nav.push({ name: "signin", then });
  return (
    <div className="upgrade-notice" role="note" data-feature={feature}>
      <div className="t">{UPGRADE_TITLE}</div>
      <div className="m">{body}</div>
      {signInAvailable() && (
        <div className="actions">
          <button className="btn sm upgrade-primary" onClick={go}>
            Create account
          </button>
          <button className="btn sm upgrade-secondary" onClick={go}>
            Sign in
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The sign-in call laid over a screen a guest cannot use: the screen behind is blurred right out (nothing to read or tap) and the
 * notice sits on top of it, centred. Put it inside a `position: relative` host that holds the screen's content.
 */
export function GateOverlay({ nav, then, feature }: { nav: Nav; then: View; feature: Feature }) {
  return (
    <div className="gate-overlay" data-feature={feature}>
      <UpgradeNotice nav={nav} then={then} feature={feature} />
    </div>
  );
}

/** What the feature looks like, drawn from made-up rows, so a guest sees what they would get (blurred, behind the notice). */
export function Preview({ feature }: { feature: Feature }) {
  if (feature === "git") return <GitDemo />;
  if (feature === "history.conversation")
    return (
      <div className="pv-chat">
        <div className="pv-b user">Add rate limiting to the /login endpoint and cover it with tests.</div>
        <div className="pv-b">I’ll add a token-bucket limiter keyed by IP and username, then wire it in.</div>
        <div className="pv-b">Done. /login now allows 5 attempts per minute and returns 429 with a Retry-After header.</div>
        <div className="pv-b user">Great, also log the blocked attempts.</div>
      </div>
    );
  return (
    <div className="card pv-approval">
      <div className="pv-k A">APPROVAL · Claude</div>
      <div className="pv-t">Claude wants to run a command</div>
      <div className="pv-code">npm test -- auth{"\n"}Run the auth test suite</div>
      <div className="pv-actions">
        <span className="pv-ok">Approve</span>
        <span className="pv-no">Deny</span>
      </div>
    </div>
  );
}

/** The feature itself, shown with example data under a light blur, with the sign-in call floating at the bottom. */
export function Gated({ nav, feature, then, children }: { nav: Nav; feature: Feature; then: View; children?: ReactNode }) {
  return (
    <div className="gated" data-feature={feature}>
      <div className="gated-back">{children ?? <Preview feature={feature} />}</div>
      <div className="gated-front">
        <UpgradeNotice nav={nav} then={then} feature={feature} />
      </div>
    </div>
  );
}

/** A whole screen standing in for a feature a guest can't use yet. */
export function LockedScreen({ nav, title, feature, then }: { nav: Nav; title: string; feature: Feature; then: View }) {
  return (
    <div className="screen" data-screen="locked" data-feature={feature}>
      <div className="safe-top">
        <div className="topbar">
          <BackButton onClick={nav.back} />
          <div className="title-col">
            <div className="t">{title}</div>
          </div>
        </div>
      </div>
      <div className="scroll">
        <div className="content" style={{ padding: "16px 16px 32px" }}>
          <Gated nav={nav} feature={feature} then={then} />
        </div>
      </div>
    </div>
  );
}
